import { CdkScrollable } from '@angular/cdk/scrolling';
import { DatePipe } from '@angular/common';
import { afterNextRender, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, OnInit, signal, viewChild, viewChildren } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Applicant, AskEvaQueryRequest, Policy } from 'api-clients/ai-api-client';
import { map, merge, of, skip, startWith, switchMap } from 'rxjs';
import { EvaSuggestedPromptsService } from 'src/app/eva-sidebar/eva-suggested-prompts/eva-suggested-prompts.service';
import { EzAlertComponent } from 'src/shared/ez-alert/ez-alert.component';
import { materialModules } from 'src/shared/material.module';
import { UserService } from 'src/shared/user.service';
import { globalWindow } from 'src/shared/window-globals';
import { EvaContextComponent } from '../eva-context/eva-context.component';
import { EvaPromptComponent, EvaPromptSubmittedEvent } from '../eva-prompt/eva-prompt.component';
import { EvaSidebarService } from '../eva-sidebar.service';
import { EvaAnswerDto, EvaAnswerType } from '../shared/models/eva-question-answer.model';
import { EvaPageContextService } from '../shared/services/eva-page-context.service';
import { EvaQuestionAnswerService } from '../shared/services/eva-question-answer.service';
import { EvaActionConfirmationComponent } from './eva-action-confirmation/eva-action-confirmation.component';
import { EvaApplicantsComponent } from './eva-applicants/eva-applicants.component';
import { EvaFeedbackComponent } from './eva-feedback/eva-feedback.component';
import { EvaPoliciesComponent } from './eva-policies/eva-policies.component';
import { EvaTextFormatterComponent } from './eva-text-formatter/eva-text-formatter.component';


@Component({
  selector: 'ez-eva-sidebar-content',
  imports: [
    EvaPromptComponent,
    EvaTextFormatterComponent,
    EvaApplicantsComponent,
    EvaPoliciesComponent,
    EvaActionConfirmationComponent,
    EzAlertComponent,
    CdkScrollable,
    materialModules,
    EvaFeedbackComponent,
    DatePipe,
    EvaContextComponent,
  ],
  templateUrl: './eva-sidebar-content.component.html',
  styleUrl: './eva-sidebar-content.component.scss',
})
export class EvaSidebarContentComponent implements OnInit {

  protected readonly firstName = signal('');
  readonly #evaSidebarService = inject(EvaSidebarService);
  readonly #userService = inject(UserService);
  readonly #destroyRef = inject(DestroyRef);
  protected readonly isCallingApi = computed(() => this.#evaQuestionAnswerService.isCallingApi());
  protected readonly isInitialized = signal(false);
  protected readonly isLoading = computed(() => !this.isInitialized() || this.#evaQuestionAnswerService.isLoadingHistory());
  protected readonly isExpanded = this.#evaSidebarService.isExpanded;
  readonly #evaQuestionAnswerService = inject(EvaQuestionAnswerService);
  readonly #pageContextService = inject(EvaPageContextService);
  readonly #evaSuggestedPromptsService = inject(EvaSuggestedPromptsService);
  readonly #injector = inject(Injector);
  protected readonly evaAnswerTypes = EvaAnswerType;
  protected readonly questionsAnswers = computed(() =>
    this.#evaQuestionAnswerService.questionAnswers()
  );
  protected readonly hasPendingConfirmation = computed(() =>
    this.#evaQuestionAnswerService.hasPendingConfirmation()
  );
  protected readonly isProcessingAction = computed(() =>
    this.#evaQuestionAnswerService.isProcessingAction()
  );
  protected currentApplicantId = computed<number>(() =>
    this.questionsAnswers()?.applicantId ?? null
  );
  protected readonly isCatchMeUpRepressed = signal(false);
  protected readonly catchMeUpHintIndex = signal<number | null>(null);
  protected hintMessage = `Get client summaries from any time in the last year.
  Just prompt the time period (like 24 hours or 12 months) and pick which sources to summarize, such as emails, texts, or notes.`;
  protected readonly serviceCatchMeUpIndex = signal<number | null>(null);
  readonly #isFirstQuestionInNewChat = signal<boolean>(false);
  readonly showCatchMeUpHint = computed(() => {
    return ($index: number): boolean =>
      !this.isCatchMeUpRepressed() &&
      (this.catchMeUpHintIndex() === $index ||
        this.serviceCatchMeUpIndex() === $index);
  });
  protected hasQuestions = computed(() => !!this.questionsAnswers()?.questionAndAnswers?.length);
  protected readonly isContextChipVisible = this.#pageContextService.isAnchorVisible;

  /** Suggested prompts to render, already gated for visibility. */
  protected readonly visiblePromptPills = this.#evaSuggestedPromptsService.visiblePrompts;
  /** Static prompts to hide, e.g. ones the current page's own prompts duplicate. */
  protected readonly excludedStaticPrompts = this.#evaSuggestedPromptsService.excludedStaticPrompts;


  protected readonly conversationFeed = viewChild('conversationFeed', { read: CdkScrollable });
  protected readonly questionItems = viewChildren<ElementRef<HTMLElement>>('questionItem');
  protected readonly isScrollAtBottom = toSignal(
    toObservable(this.conversationFeed).pipe(
      switchMap((feed) => {
        if (!feed) return of(true);
        const el: HTMLElement = feed.getElementRef().nativeElement;
        return merge(
          feed.elementScrolled(),
          toObservable(this.questionsAnswers, { injector: this.#injector }).pipe(skip(1))
        ).pipe(
          startWith(null),
          map(() => el.scrollHeight - el.scrollTop - el.clientHeight <= 1)
        );
      })
    ),
    { initialValue: true }
  );
  protected readonly showScrollButton = computed(() =>
    this.#evaQuestionAnswerService.hasNewAnswer() && !this.isScrollAtBottom() && !this.#autoScroll()
  );
  // Scroll-only — content changes are handled by isScrollAtBottom;
  // keeping them separate prevents DOM insertions from flipping #autoScroll mid-API-call.
  readonly #isUserScrollAtBottom = toSignal(
    toObservable(this.conversationFeed).pipe(
      switchMap((feed) => {
        if (!feed) return of(true);
        const el: HTMLElement = feed.getElementRef().nativeElement;
        return feed.elementScrolled().pipe(
          startWith(null),
          map(() => el.scrollHeight - el.scrollTop - el.clientHeight <= 1)
        );
      })
    ),
    { initialValue: true }
  );
  readonly #autoScroll = signal(true);
  readonly #loadingMessages = [
    'EVA is working on your answer…',
    'Just a moment while EVA checks the details…',
    'Getting this ready for you now…',
  ];

  #loadingIndex = 0;
  #suggestionsFetchedForThreadId: string | null = null;
  protected readonly loadingMessage = signal('');
  #lastSentContextKey: string | null = null;
  constructor() {
    effect(() => {
      const threadId = this.#evaSidebarService.threadId();
      const loadedThreadId = this.questionsAnswers()?.threadId;
      // Keyed on thread identity (not message count) so an empty thread still counts as loaded and
      // can't reload forever. Case-insensitive: the list/dropdown upper-case the GUID, the endpoint
      // echoes it lower-case.
      const showsRequestedThread = !!loadedThreadId
        && loadedThreadId.toLowerCase() === threadId?.toLowerCase();
      // Load a newly-selected thread (initial open, recent-chats, View chat). Same thread = no-op.
      if (threadId && !showsRequestedThread) {
        this.#isFirstQuestionInNewChat.set(false);
        this.#loadThreadHistory();
      } else {
        this.isInitialized.set(true);
      }
    });

    effect(() => {
      if (this.isCallingApi()) {
        this.loadingMessage.set(this.#loadingMessages[this.#loadingIndex++ % this.#loadingMessages.length]);
      }
    });

    effect(() => {
      // Guard on hasNewAnswer so clearing the flags (which mutates questionAnswers and
      // feeds back into isScrollAtBottom) doesn't cause the effect to re-evaluate needlessly.
      if (this.isScrollAtBottom() && this.#evaQuestionAnswerService.hasNewAnswer()) {
        this.#evaQuestionAnswerService.hasNewAnswer.set(false);
        this.#evaQuestionAnswerService.clearNewResponseFlags();
      }
    });

    effect(() => {
      if (this.isCallingApi()) {
        this.#autoScroll.set(this.#isUserScrollAtBottom());
      }
    });
  }

  ngOnInit(): void {
    this.#evaSidebarService.onNewChat$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#onNewChat();
    });

    this.#loadUserName().catch(() => {
      this.firstName.set('');
    });

    this.#evaSidebarService.onSendQuestion$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((question: string) => {
      if (question?.trim()) {
        this.sendCurrentQuestion({ prompt: question }, globalWindow.applicantContext?.ApplicantID);
      }
    });

    this.serviceCatchMeUpIndex.set(this.#evaSidebarService.getCatchMeUpIndex());

    this.#evaSidebarService.onCatchMeUp$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((enabled) => {
      if (this.serviceCatchMeUpIndex() === null && this.catchMeUpHintIndex() === null && enabled) {
        const data = this.questionsAnswers()?.questionAndAnswers;
        if (data?.length) {
          this.catchMeUpHintIndex.set(data.length - 1);
          this.#evaSidebarService.setCatchMeUpIndex(this.catchMeUpHintIndex());
        }
      }
    });

    this.#evaSidebarService.onCatchMeUpRepressed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((repressed) => {
      this.isCatchMeUpRepressed.set(repressed);
    });
  }

  protected scrollToNewResponse(): void {
    const feed = this.conversationFeed();
    if (!feed) return;
    const container = feed.getElementRef().nativeElement as HTMLElement;
    const newResponseIndex = this.questionsAnswers().questionAndAnswers.findIndex(qa => qa.isNewResponse);
    const newResponseEl = this.questionItems()[newResponseIndex]?.nativeElement;
    if (!newResponseEl) {
      feed.scrollTo({ bottom: 0, behavior: 'smooth' });
    } else {
      const scrollTop = container.scrollTop + (newResponseEl.getBoundingClientRect().top - container.getBoundingClientRect().top) - 25;
      feed.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
    this.#evaQuestionAnswerService.hasNewAnswer.set(false);
    this.#evaQuestionAnswerService.clearNewResponseFlags();
  }

  protected async sendCurrentQuestion(promptSubmitted: EvaPromptSubmittedEvent, applicantId?: number): Promise<void> {
    const question = promptSubmitted.prompt;
    if (!question) {
      return;
    }

    const query: AskEvaQueryRequest = { query: question, threadId: this.questionsAnswers()?.threadId };
    const finalApplicantId = this.#determineApplicantId(applicantId);

    if (finalApplicantId) {
      query.applicantId = finalApplicantId;
    }

    this.#evaQuestionAnswerService.addQuestion(question, finalApplicantId ?? this.currentApplicantId());
    this.#autoScroll.set(true);
    this.#keepPinnedToBottom();
    const answer = await this.#sendQuery(query, question, finalApplicantId ?? this.currentApplicantId());
    if (answer) {
      this.#evaSidebarService.threadId.set(this.questionsAnswers()?.threadId || null);
    } else {
      this.#evaQuestionAnswerService.addStopConfirmation();
    }
    if (this.#autoScroll()) {
      afterNextRender(() => this.#scrollFeedToBottom(), { injector: this.#injector });
    }
  }

  protected async onSuggestedPromptSelected(prompt: string): Promise<void> {
    await this.sendCurrentQuestion({ prompt });
  }

  async #sendQuery(
    query: AskEvaQueryRequest, questionText: string, applicantId: number | null, options?: { isAction?: boolean },
  ): Promise<EvaAnswerDto | undefined> {
    const { query: activeQuery, contextKey } = this.#applyActiveContext(query);
    const answer = await this.#evaQuestionAnswerService.processQuery(activeQuery, options);
    this.#refreshSuggestions(answer);
    if (answer) {
      this.#evaQuestionAnswerService.updateQuestionAnswers(questionText, answer, applicantId);
      this.#pageContextService.setResolvedContext(answer.context);
      if (contextKey !== undefined) {
        this.#lastSentContextKey = contextKey;
      }
    }
    return answer;
  }

  #applyActiveContext(query: AskEvaQueryRequest): { query: AskEvaQueryRequest; contextKey?: string } {
    if (this.#pageContextService.isExplicitlyCleared()) {
      return this.#applyDedupedContext(query, -1, -1);
    }

    const context = this.#pageContextService.contextForQuery();
    if (!context) {
      return { query };
    }

    const applicantId = query.applicantId ?? context.applicantId ?? undefined;
    const policyId = query.policyId ?? context.policyId ?? undefined;

    if (query.applicantId != null || query.policyId != null) {
      return { query: { ...query, applicantId, policyId }, contextKey: this.#contextKey(applicantId, policyId) };
    }

    return this.#applyDedupedContext(query, applicantId, policyId);
  }

  #applyDedupedContext(
    query: AskEvaQueryRequest, applicantId: number | undefined, policyId: number | undefined,
  ): { query: AskEvaQueryRequest; contextKey?: string } {
    const key = this.#contextKey(applicantId, policyId);
    if (key === this.#lastSentContextKey) {
      return { query };
    }
    return { query: { ...query, applicantId, policyId }, contextKey: key };
  }

  #contextKey(applicantId: number | null | undefined, policyId: number | null | undefined): string {
    return `${applicantId ?? ''}|${policyId ?? ''}`;
  }

  #determineApplicantId(applicantId?: number): number | null {
    if (applicantId) {
      // Explicit applicant ID provided
      return applicantId;
    }

    if (this.#isFirstQuestionInNewChat()) {
      // First question in new chat - always send current global applicant ID
      this.#isFirstQuestionInNewChat.set(false);
      const globalApplicantId = this.#pageContextService.contextForQuery()?.applicantId;
      if (globalApplicantId) {
        return globalApplicantId;
      }
    } else {
      // Use the eva sidebar service to determine if applicant ID should be included
      const applicantIdForRequest = this.#evaSidebarService.resolveAndSyncApplicantId();
      if (applicantIdForRequest !== null) {
        return applicantIdForRequest;
      }
    }

    return null;
  }

  async onApplicantSelected(applicant: Applicant, questionAnswerIndex: number): Promise<void> {
    const question = applicant.displayName;
    const applicantId = applicant.applicantId;
    if (!question) {
      return;
    }
    const query: AskEvaQueryRequest = {
      query: question,
      threadId: this.questionsAnswers()?.threadId,
      applicantId: applicantId ? applicantId : undefined,
    };
    this.#evaQuestionAnswerService.addQuestion(question, applicantId);
    this.#evaQuestionAnswerService.setApplicantIdByQuestionAnswerIndex(questionAnswerIndex, applicantId);
    this.#autoScroll.set(true);
    this.#keepPinnedToBottom();

    const answer = await this.#sendQuery(query, question, applicantId);
    if (answer && this.#autoScroll()) {
      afterNextRender(() => this.#scrollFeedToBottom(), { injector: this.#injector });
    }
  }

  protected async onPolicySelected(policy: Policy, questionAnswerIndex: number): Promise<void> {
    if (!policy.policyNumber || !policy.policyMasterID) {
      return;
    }
    const question = `give me the policy details for ${policy.policyNumber}`;
    const applicantId = policy.applicantId;
    const policyId = policy.policyMasterID;
    const query: AskEvaQueryRequest = {
      query: question,
      threadId: this.questionsAnswers()?.threadId,
      applicantId: applicantId ? applicantId : undefined,
      policyId,
    };
    this.#evaQuestionAnswerService.addQuestion(question, applicantId);
    this.#evaQuestionAnswerService.setPolicyIdByQuestionAnswerIndex(questionAnswerIndex, policyId);
    this.#autoScroll.set(true);
    this.#keepPinnedToBottom();

    const answer = await this.#sendQuery(query, question, applicantId);
    if (answer && this.#autoScroll()) {
      afterNextRender(() => this.#scrollFeedToBottom(), { injector: this.#injector });
    }
  }

  /**
   * Handles a HITL confirmation click (EZ-186935). Sends the chosen option back to EVA with is_hitl
   * set, locks the option set, and suppresses Stop Response + the input until the response lands.
   */
  protected async onActionOptionSelected(option: string, questionAnswerIndex: number): Promise<void> {
    if (this.isCallingApi()) {
      return;
    }

    const entry = this.questionsAnswers().questionAndAnswers[questionAnswerIndex];
    if (!entry?.answer?.hitl?.required) {
      return;
    }

    this.#evaQuestionAnswerService.confirmActionSelection(questionAnswerIndex, option);

    const applicantId = entry.applicantId ?? this.currentApplicantId();
    const query: AskEvaQueryRequest = {
      query: option,
      threadId: this.questionsAnswers()?.threadId,
      applicantId: applicantId ? applicantId : undefined,
      isHitl: true,
    };

    this.#evaQuestionAnswerService.addQuestion(option, applicantId);
    this.#autoScroll.set(true);
    this.#keepPinnedToBottom();

    const answer = await this.#sendQuery(query, option, applicantId, { isAction: true });
    if (answer && this.#autoScroll()) {
      afterNextRender(() => this.#scrollFeedToBottom(), { injector: this.#injector });
    }
  }

  async #loadUserName(): Promise<void> {
    const user = await this.#userService.getUser();
    this.firstName.set(user.firstName);
  }

  async #loadThreadHistory(): Promise<void> {
    this.isInitialized.set(false);
    // reset autoscroll
    this.#autoScroll.set(true);
    this.#lastSentContextKey = null;
    const openedViaHistory = this.#evaSidebarService.consumeOpenedViaHistory();
    // Clears the previous thread's suggestions before loading.
    this.#evaSuggestedPromptsService.abort();
    try {
      // loadThreadHistory cancels superseded loads and pins the panel to the requested thread
      // (empty threads included), so no stale reconcile is needed here (EZ-188788).
      await this.#evaQuestionAnswerService.loadThreadHistory(this.#evaSidebarService.threadId());
      const restoredContext = this.questionsAnswers().questionAndAnswers
        .findLast(qa => qa.answer?.context)?.answer?.context;
      this.#pageContextService.setResolvedContext(restoredContext ?? null, { reassertPriority: openedViaHistory });
      this.#lastSentContextKey = restoredContext
        ? this.#contextKey(restoredContext.applicantId, restoredContext.policyId)
        : null;
      const entries = this.questionsAnswers()?.questionAndAnswers ?? [];
      const lastRequestId = entries[entries.length - 1]?.answer?.requestId;
      const threadId = this.questionsAnswers()?.threadId;
      if (lastRequestId && threadId && this.#suggestionsFetchedForThreadId !== threadId) {
        this.#suggestionsFetchedForThreadId = threadId;
        this.#evaSuggestedPromptsService.fetchSuggestions(lastRequestId, threadId);
      }
    } catch {
      this.#evaSidebarService.threadId.set(null);
      this.#evaSuggestedPromptsService.abort();
    } finally {
      this.isInitialized.set(true);
      this.#scrollAfterNextRender();
    }
  }

  /**
   * Fetches suggestions for the turn that just completed, or clears any stale suggestions from a
   * previous turn when this turn produced no answer (e.g. Stop Response) — otherwise the previous
   * turn's suggestions would keep showing once the UI un-gates.
   */
  async #refreshSuggestions(answer: EvaAnswerDto | undefined): Promise<void> {
    if (!answer?.requestId || !answer?.threadId) {
      this.#evaSuggestedPromptsService.abort();
      return;
    }
    try {
      await this.#evaSuggestedPromptsService.fetchSuggestions(answer.requestId, answer.threadId);
    } catch {
      this.#evaSuggestedPromptsService.abort();
    }
  }

  #onNewChat(): void {
    this.#evaQuestionAnswerService.abort();
    this.#evaSuggestedPromptsService.abort();
    this.#evaQuestionAnswerService.resetQuestionAnswers('', null);
    this.#isFirstQuestionInNewChat.set(true);
    this.#lastSentContextKey = null;
    this.#suggestionsFetchedForThreadId = null;
  }

  protected onTextFormatterRendered(): void {
    // only when the autoScroll is true scroll to bottom.
    if (this.#autoScroll()) {
      this.#scrollFeedToBottom();
    }
  }

  #scrollFeedToBottom(): void {
    const feed = this.conversationFeed();
    if (!feed) return;
    feed.scrollTo({ bottom: 0, behavior: 'smooth' });
  }

  #scrollAfterNextRender(): void {
    afterNextRender(() => this.#scrollFeedToBottom(), { injector: this.#injector });
  }

  #keepPinnedToBottom(): void {
    if (this.#isUserScrollAtBottom()) {
      this.#scrollAfterNextRender();
    }
  }
}
